"use client";

import { ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogTrigger } from "@/components/fun-ui/Dialog";
import { Button, ButtonProps } from "@/components/fun-ui/Button";
import { cn } from "@/lib/utils";
import { useUpload } from "../hooks/useUpload";
import { BUILTIN_PLUGINS } from "../plugins";
import { UploadPlugin } from "../plugins/plugin-interface";
import { UploadConfig, UploadItem } from "../core/types";
import toast from "react-hot-toast";
import { getUploadMessages } from "../locales/messages";
import { UploadCloudIcon, ZoteroIcon } from "@/components/fun-ui/icons";
import { getAcceptFormatsText } from "../config/upload-config";
import { validateCountBeforeUpload } from "../core/upload-validator";
import type { OnBeforeUpload, PaperUploadBatchResult } from "../hooks/useUpload";
import {
  isNativeUploadDialogRuntime,
  NativeSelectionError,
  pickNativePdfFiles,
} from "@/lib/desktop/file-dialogs";
import Loading from "@/components/Loading";

const messages = getUploadMessages();

/**
 * PaperUpload 组件变体
 * - "button": 仅渲染一个按钮，点击后打开弹窗显示上传界面
 * - "area": 直接渲染上传区域内容（不通过弹窗）
 * 
 * 注意：
 * - 无论 variant 是什么，只要传入 dropArea prop，都可以让该区域形成大面积拖拽区
 * - "大面积拖拽区"不是 variant 的区别，而是通过 dropArea 实现的
 */
export type PaperUploadVariant = "area" | "button";

/**
 * PaperUpload 组件统一接口
 * 
 * 职责：
 * 1. 统一的文献上传组件入口，通过 variant 配置实现不同渲染方式
 *    - variant="button": 渲染按钮，点击打开弹窗
 *    - variant="area": 直接渲染上传区域内容
 * 2. 支持外部拖拽区域的事件绑定（在组件初始化时就完成，无论 variant 是什么）
 *    - 通过 dropArea prop 指定外部拖拽区域，让该区域形成大面积拖拽区
 * 3. 集成插件系统，支持灵活的功能扩展
 */
/** 插件配置对象，支持传入插件和自定义属性 */
export interface PluginWithProps {
  plugin: UploadPlugin;
  props?: Record<string, any>;
}

type PaperUploadPluginConfig = string | UploadPlugin | PluginWithProps;

export interface PaperUploadProps {
  /** 组件变体：'area' | 'button' */
  variant?: PaperUploadVariant;

  /** 启用的插件列表（可使用内置 ID 或直接传入插件实现） */
  plugins?: PaperUploadPluginConfig[];

  /** 主题 ID（如果存在，会显示导入按钮） */
  subjectId?: string;

  /** 路由标识（用于会话管理） */
  route?: string;

  /** 已上传数量（用于数量限制校验） */
  uploadedCount?: number;
  /** 已导入的文献 id 列表（传入时用于数量校验与重复判断：本次上传若在列表中则标记为 -8 已在清单内） */
  uploadedIds?: string[];
  /** 已上传的文献数量（用于区分单次/累计模式：-1 表示单次模式，>=0 表示累计模式） */
  uploadedNumOfPapers?: number;

  /** 上传前的回调（返回 null 可取消上传，返回对象可传递参数给 uploadPapers） */
  onBeforeUpload?: OnBeforeUpload;

  /** 上传完成后的回调 */
  onUploaded?: (result: PaperUploadBatchResult) => Promise<void> | void;

  /** 自定义样式类名 */
  className?: string;

  /** 自定义子元素（如导入按钮） */
  children?: ReactNode;

  /**
   * 外部拖拽区域（可选）
   * - 如果提供，会在组件初始化时绑定事件到该区域
   * - 无论 variant 是什么，都能在该区域响应拖拽
   * - 拖拽时会显示遮罩层，并可以直接上传
   */
  dropArea?: React.RefObject<HTMLElement | null> | HTMLElement | null;

  // Button 模式专用配置
  /** 弹窗触发按钮的文本（仅 button 模式） */
  label?: ReactNode;

  /** 弹窗触发按钮的属性（仅 button 模式） */
  buttonProps?: ButtonProps;

  /** 弹窗触发按钮的 className（仅 button 模式） */
  buttonClassName?: string;

  /** 是否在外部拖拽时自动打开 Dialog（仅 button 模式，默认 false，直接上传） */
  openDialogOnDrag?: boolean;
}

/**
 * PaperUpload 组件暴露的方法接口
 */
export interface PaperUploadRef {
  /** 打开系统文件选择器（支持文件和文件夹） */
  openFilePicker: (options?: { preferFolder?: boolean }) => void;
  /** 打开弹窗 */
  open: () => void;
}

/**
 * PaperUpload - 统一的文献上传组件
 * 
 * 功能特性：
 * 1. 通过 variant 配置实现不同渲染方式：
 *    - variant="button": 渲染按钮，点击打开弹窗
 *    - variant="area": 直接渲染上传区域内容
 * 2. 外部拖拽区域的事件绑定在组件初始化时就完成，确保正确识别
 *    - 无论 variant 是什么，只要传入 dropArea，都可以让该区域形成大面积拖拽区
 * 3. 统一的插件系统和配置管理
 * 
 * 使用场景：
 * - variant="area": PaperList 空列表、SubjectResearchLayout 主题页（直接渲染上传区域）
 * - variant="button": Toolbar 工具栏、WorkflowCreator 工作流（按钮+弹窗）
 */
export const PaperUpload = forwardRef<PaperUploadRef, PaperUploadProps>(function PaperUpload({
  variant = "button",
  plugins = ["zotero", "local-file", "local-folder", "url"],
  subjectId,
  route,
  uploadedCount,
  uploadedIds,
  uploadedNumOfPapers = -1,
  onBeforeUpload,
  onUploaded,
  className,
  children,
  dropArea,
  // Button 模式专用配置
  label = messages.buttons.import,
  buttonProps,
  buttonClassName,
  openDialogOnDrag = false,
}, ref) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [externalDragging, setExternalDragging] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 根据 uploadedNumOfPapers 决定是单次模式还是累计模式
  // uploadedNumOfPapers === -1 表示单次模式，uploadedCount 应该为 0 或 undefined
  // uploadedNumOfPapers >= 0 表示累计模式，uploadedCount 应该为 uploadedNumOfPapers
  // 传入 uploadedIds 时不需要再传 uploadedCount，useUpload 内部通过 uploadedIds.length 获取数量
  const isCumulativeMode = uploadedNumOfPapers >= 0;
  const effectiveUploadedCount = uploadedIds?.length ?? (uploadedNumOfPapers >= 0 ? uploadedNumOfPapers : (uploadedCount ?? 0));

  const { handleUpload, config, isSubmitting } = useUpload({
    route,
    subjectId,
    uploadedCount: uploadedIds != null ? undefined : effectiveUploadedCount,
    uploadedIds,
    onBeforeUpload,
    onUploaded,
  });

  /**
   * onBeforeUpload 协作说明：
   * 
   * - PaperUpload 不直接关心「AI 表格列消耗」等业务，只把当前选择的 files 传给 useUpload
   * - useUpload 在本地校验(validFiles/invalidFiles)之前、真正开始上传之前调用 onBeforeUpload(files)
   *   - 若 onBeforeUpload 返回 null，则整轮上传会被取消（包括 OSS 上传和面板追加）
   *   - 若返回对象（如 { ignoreAiCol: true }），对象中的属性会传递给 uploadPapers
   *   - 返回空对象 {} 表示正常上传，不传递额外参数
   * - 具体业务（比如弹出 ConfirmPriceDialog、设置 confirm/cancel 回调）由外层组件（如 PaperList）实现
   * - 重要：onBeforeUpload 在 validateFiles 之前执行，因此此时尚未创建任何 UploadItem，
   *   面板不会显示，用户可以在确认对话框中安心选择，不会看到面板突然弹出
   */

  // 共享的文件模式 input ref（用于 LocalUploadArea 和 local-file-plugin）
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 共享的文件夹模式 input ref（用于 local-folder-plugin）
  const folderInputRef = useRef<HTMLInputElement>(null);

  // 接受格式文案（用于提示「拖动 XXX 文件或文件夹上传」）
  const acceptsText = useMemo(
    () => getAcceptFormatsText(config.formats),
    [config.formats]
  );

  // 文件类型过滤属性
  const acceptAttr = useMemo(
    () =>
      config.formats
        .map((format) => [format.accept, ...format.extensions])
        .flat()
        .join(","),
    [config.formats]
  );

  // 获取启用的插件（支持 {plugin, props} 格式）
  const enabledPlugins = useMemo(() => {
    const context = {
      subjectId,
      uploadedCount: uploadedIds?.length ?? uploadedCount ?? 0,
      config,
    };
    return plugins
      .map((entry) => {
        if (typeof entry === "string") {
          return { plugin: BUILTIN_PLUGINS[entry], props: undefined };
        }
        // 检查是否是 {plugin, props} 格式
        if (entry && typeof entry === "object" && "plugin" in entry) {
          const pluginWithProps = entry as PluginWithProps;
          return {
            plugin: pluginWithProps.plugin,
            props: pluginWithProps.props,
          };
        }
        // 普通 UploadPlugin 对象
        return { plugin: entry as UploadPlugin, props: undefined };
      })
      .filter((item): item is { plugin: UploadPlugin; props: Record<string, any> | undefined } => {
        if (!item || !item.plugin) return false;
        if (item.plugin.isSupported) {
          return item.plugin.isSupported(context);
        }
        return true;
      });
  }, [plugins, subjectId, uploadedCount, uploadedIds, config]);

  // 分离 URL 插件、本地文件/文件夹插件和其他插件
  const urlPlugin = enabledPlugins.find((item) => item.plugin.id === "url");
  const localFilePlugin = enabledPlugins.find((item) => item.plugin.id === "local-file");
  const localFolderPlugin = enabledPlugins.find((item) => item.plugin.id === "local-folder");
  const hasZoteroPlugin = enabledPlugins.some((item) => item.plugin.id === "zotero");
  const otherPlugins = enabledPlugins.filter(
    (item) => item.plugin.id !== "url" && item.plugin.id !== "local-file" && item.plugin.id !== "local-folder"
  );

  // 统一处理本地/文件夹选择的文件（点击 input 或拖拽）
  const processLocalFiles = useCallback(
    async (
      fileList: FileList | File[] | null,
      options?: { fromDirectory?: boolean }
    ) => {
      if (!fileList) {
        return;
      }
      const selection = collectUploadSelection(fileList, {
        config,
        uploadedCount: effectiveUploadedCount,
        fromDirectory: options?.fromDirectory,
      });

      if (selection.exceeded) {
        toast.error(selection.errorMessage);
        return;
      }

      if (!selection.files.length) {
        if (options?.fromDirectory) {
          toast.error(messages.errors.noFilesInFolder);
        }
        return;
      }

      // 不在入口侧按扩展名过滤文件，统一交给 validateFiles 做格式校验：
      // - 所有被选择/拖拽进来的文件（包括非 PDF）都会进入 UploadProgressPanel
      // - 非 PDF 文件在校验阶段会被标记为 FORMAT_INVALID，并展示为“非 PDF，无法上传”
      await handleUpload(
        selection.files,
        options?.fromDirectory ? "local-folder" : "local",
      );
    },
    [config, effectiveUploadedCount, handleUpload]
  );

  const openBrowserFilePicker = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) return;
    input.removeAttribute("webkitdirectory");
    input.setAttribute("accept", acceptAttr);
    input.click();
  }, [acceptAttr]);

  const openBrowserFolderPicker = useCallback(() => {
    const input = folderInputRef.current;
    if (!input) return;
    input.setAttribute("webkitdirectory", "");
    input.setAttribute("accept", acceptAttr);
    input.click();
  }, [acceptAttr]);

  const openNativePicker = useCallback(async (fromDirectory: boolean) => {
    try {
      const remainingByTotal = Math.max(
        0,
        (config.maxTotal ?? Number.POSITIVE_INFINITY) - effectiveUploadedCount,
      );
      const allowedCount = Math.min(config.maxFiles, remainingByTotal);
      const files = await pickNativePdfFiles({
        selectionKind: fromDirectory ? "folder" : "files",
        // 额外返回一项，让现有 collectUploadSelection 负责显示统一的数量错误。
        maxFiles: Math.max(1, allowedCount + 1),
        maxFileSizeMb: config.maxFileSize,
      });
      if (files === null) {
        return false;
      }
      if (files.length) {
        await processLocalFiles(files, { fromDirectory });
      }
      return true;
    } catch (error) {
      console.error("[Findin Desktop] native upload selection failed", error);
      if (error instanceof NativeSelectionError) {
        const limitMb = Math.max(1, Math.floor(error.limitBytes / (1024 * 1024)));
        toast.error(error.code === "FILE_SIZE_EXCEEDED"
          ? messages.errors.nativeFileSizeExceeded(error.fileName || "PDF", limitMb)
          : messages.errors.nativeBatchSizeExceeded(limitMb));
      } else {
        toast.error(error instanceof Error ? error.message : "打开本地文件失败，请重试");
      }
      return true;
    }
  }, [config.maxFileSize, config.maxFiles, config.maxTotal, effectiveUploadedCount, processLocalFiles]);

  const openFolderPicker = useCallback(() => {
    if (!isNativeUploadDialogRuntime()) {
      openBrowserFolderPicker();
      return;
    }
    void openNativePicker(true).then((handled) => {
      if (!handled) openBrowserFolderPicker();
    });
  }, [openBrowserFolderPicker, openNativePicker]);

  // 打开系统文件选择器（默认文件模式）
  const openFilePicker = useCallback(
    (options?: { preferFolder?: boolean }) => {
      if (isSubmitting) {
        return;
      }
      if (options?.preferFolder) {
        openFolderPicker();
        return;
      }
      if (!isNativeUploadDialogRuntime()) {
        openBrowserFilePicker();
        return;
      }
      void openNativePicker(false).then((handled) => {
        if (!handled) openBrowserFilePicker();
      });
    },
    [isSubmitting, openBrowserFilePicker, openFolderPicker, openNativePicker],
  );

  // 暴露方法给外部使用
  useImperativeHandle(ref, () => ({
    openFilePicker,
    open: () => setDialogOpen(true),
  }), [openFilePicker, setDialogOpen]);

  // 获取 dropArea 元素
  const getDropAreaElement = useCallback((): HTMLElement | null => {
    if (!dropArea) {
      return null;
    }
    if ("current" in dropArea) {
      return dropArea.current;
    }
    return dropArea;
  }, [dropArea]);

  // 外部拖拽区域的事件处理（所有 variant 都支持）
  const handleExternalDragDrop = useCallback(
    (e: DragEvent): void => {
      e?.preventDefault();
      e?.stopPropagation();
      const el = e.currentTarget as HTMLElement;
      if (!el) return;

      if (isSubmitting) {
        el.dataset.dragging = "false";
        setExternalDragging(false);
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = "none";
        }
        return;
      }

      if (e.type === "dragenter" || e.type === "dragover") {
        // 重新进入/持续悬停时，取消可能已排队的 dragleave 定时器，避免闪烁
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (el.dataset.dragging !== "true") {
          el.dataset.dragging = "true";
        }
        setExternalDragging(true);
      } else if (e.type === "dragleave") {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          if (el.dataset.dragging === "true") {
            el.dataset.dragging = "false";
          }
          setExternalDragging(false);
        }, 200);
      } else if (e.type === "drop") {
        if (el.dataset.dragging === "true") {
          el.dataset.dragging = "false";
        }
        setExternalDragging(false);

        const dataTransfer = e.dataTransfer;
        if (!dataTransfer) {
          return;
        }

        const droppedUrl = dataTransfer.getData("text/plain");
        if (droppedUrl) {
          toast.error(messages.uploadArea.urlDragNotSupported);
          return;
        }

        const dropHasDirectory = hasDirectoryInDataTransfer(e);
        const hasFiles = dataTransfer.files && dataTransfer.files.length > 0;

        if (!dropHasDirectory && hasFiles) {
          // 纯文件拖拽，沿用原有逻辑
          if (variant === "button" && openDialogOnDrag) {
            setDialogOpen(true);
          } else {
            processLocalFiles(dataTransfer.files, {
              fromDirectory: false,
            });
          }
          return;
        }

        if (dropHasDirectory) {
          // 拖拽的是文件夹（或包含文件夹），需要通过 entry 递归读取所有文件
          const handleDirectoryDrop = async () => {
            const filesFromEntries = await collectFilesFromDataTransfer(dataTransfer);
            if (!filesFromEntries.length && hasFiles) {
              // 回退：有 files 但没读到 entry，走统一入口做隐藏文件过滤和数量保护
              await processLocalFiles(dataTransfer.files, { fromDirectory: true });
              return;
            }
            await processLocalFiles(filesFromEntries, { fromDirectory: true });
          };

          if (variant === "button" && openDialogOnDrag) {
            setDialogOpen(true);
          }
          // 无论是否自动打开 Dialog，都并行触发文件收集与上传
          void handleDirectoryDrop();
        }
      }
    },
    [isSubmitting, processLocalFiles, variant, openDialogOnDrag]
  );

  const preventDragDrop = useCallback((e: DragEvent) => {
    e?.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "none";
      e.dataTransfer.dropEffect = "none";
    }
  }, []);

  // 在组件初始化时就绑定事件到外部 dropArea（无论 variant 是什么）
  useEffect(() => {
    const dropAreaElement = getDropAreaElement();
    if (!dropAreaElement) {
      return;
    }

    dropAreaElement.addEventListener("dragstart", preventDragDrop, false);
    dropAreaElement.addEventListener("dragenter", handleExternalDragDrop, false);
    dropAreaElement.addEventListener("dragover", handleExternalDragDrop, false);
    dropAreaElement.addEventListener("dragleave", handleExternalDragDrop, false);
    dropAreaElement.addEventListener("drop", handleExternalDragDrop, false);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      dropAreaElement.removeEventListener("dragstart", preventDragDrop);
      dropAreaElement.removeEventListener("dragenter", handleExternalDragDrop);
      dropAreaElement.removeEventListener("dragover", handleExternalDragDrop);
      dropAreaElement.removeEventListener("dragleave", handleExternalDragDrop);
      dropAreaElement.removeEventListener("drop", handleExternalDragDrop);
    };
  }, [getDropAreaElement, handleExternalDragDrop, preventDragDrop]);

  // 渲染外部拖拽遮罩层
  const renderExternalDragOverlay = () => {
    const overlay = (
      <>
        <div className="absolute inset-0 z-40 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 pointer-events-none" />
        <div className="absolute z-50 min-w-[220px] w-[20%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="border-primary border rounded-lg bg-[#F1F4FE] text-primary p-8 flex flex-col justify-center items-center pointer-events-none">
            <UploadCloudIcon height="32" className="animate-bounce" />
            <p className="mt-2 text-sm">{messages.uploadArea.dropToUpload}</p>
          </div>
        </div>
      </>
    );

    const dropAreaElement = getDropAreaElement();
    if (dropAreaElement) {
      // 确保 dropArea 有 relative 定位
      const computedStyle = window.getComputedStyle(dropAreaElement);
      if (computedStyle.position === "static") {
        dropAreaElement.style.position = "relative";
      }
      return createPortal(overlay, dropAreaElement);
    }
    return null;
  };

  // 处理文件夹选择的文件（遍历文件夹中的文件，类似拖拽逻辑）
  const handleFolderSelection = useCallback(
    async (fileList: FileList | null) => {
      // 统一复用 processLocalFiles 逻辑，让文件夹选择与拖拽保持一致行为
      await processLocalFiles(fileList, { fromDirectory: true });
    },
    [processLocalFiles]
  );

  // 插件按钮渲染
  const pluginButtons = (
    <div
      className="flex flex-wrap gap-3"
      onClick={(e) => {
        // 阻止插件按钮区域的点击事件冒泡到 LocalUploadArea
        e.stopPropagation();
      }}
    >
      {otherPlugins.map((item) => (
        <div key={item.plugin.id} className="flex-initial">
          {item.plugin.renderTrigger({
            config,
            disabled: isSubmitting,
            uploadedCount: effectiveUploadedCount,
            subjectId,
            onUpload: (files) => handleUpload(files, pluginToSource(item.plugin)),
            openFilePicker,
            pluginConfig: item.props || item.plugin.pluginConfig,
            onSubDialogChange: variant === "button" ? (open) => {
              setSubDialogOpen(open);
              if (!open) {
                // 当子 Dialog 关闭时，也关闭父级 Dialog
                setDialogOpen(false);
              }
            } : undefined,
          })}
        </div>
      ))}
      {localFolderPlugin && (
        <div className="flex-initial">
          {localFolderPlugin.plugin.renderTrigger({
            config,
            disabled: isSubmitting,
            subjectId,
            onUpload: (files) => handleUpload(files, "local-folder"),
            onFileChange: handleFolderSelection,
            openFilePicker,
            folderInputRef,
            pluginConfig: localFolderPlugin.props || localFolderPlugin.plugin.pluginConfig,
          })}
        </div>
      )}
      {localFilePlugin && (
        <div className="flex-initial">
          {localFilePlugin.plugin.renderTrigger({
            config,
            disabled: isSubmitting,
            subjectId,
            onUpload: (files) => handleUpload(files, "local-file"),
            onFileChange: (files) => processLocalFiles(files, { fromDirectory: false }),
            openFilePicker,
            fileInputRef,
            pluginConfig: localFilePlugin.props || localFilePlugin.plugin.pluginConfig,
          })}
        </div>
      )}
      {children}
    </div>
  );

  // 渲染上传区域内容（Dialog 和 Area 模式共用）
  const renderUploadArea = (options?: { includeOpenFilePicker?: boolean; wrapperClassName?: string }) => {
    const { includeOpenFilePicker = false, wrapperClassName } = options || {};
    return (
      <div
        className={cn("flex flex-col gap-6 relative", wrapperClassName)}
        aria-busy={isSubmitting}
        data-paper-upload-busy={isSubmitting ? "true" : "false"}
      >
        {isSubmitting ? (
          <div
            className="absolute inset-0 z-[60] cursor-wait"
            data-paper-upload-feedback="pending"
          >
            <Loading mask zIndex={60} className="text-sm text-foreground">
              {messages.progress.preparing}
            </Loading>
          </div>
        ) : null}
        <div className="relative w-full">
          <LocalUploadArea
            subjectId={subjectId}
            acceptsText={acceptsText}
            config={config}
            pluginButtons={pluginButtons}
            openFilePicker={openFilePicker}
            openFolderPicker={openFolderPicker}
            hasLocalFilePlugin={!!localFilePlugin}
            hasLocalFolderPlugin={!!localFolderPlugin}
            processLocalFiles={processLocalFiles}
            isCumulativeMode={isCumulativeMode}
            disabled={isSubmitting}
          />
        </div>
        {urlPlugin && (
          <div className="w-full">
            {urlPlugin.plugin.renderTrigger({
              config,
              disabled: isSubmitting,
              subjectId,
              onUpload: (files) => handleUpload(files, "url"),
              ...(includeOpenFilePicker && { openFilePicker }),
              pluginConfig: urlPlugin.props || urlPlugin.plugin.pluginConfig,
            })}
          </div>
        )}
        {!hasZoteroPlugin ? (
          <div className="flex w-full justify-end -mt-2">
            {/* 不支持内置 Zotero 选择器时，保留快速上传指南作为降级入口 */}
            <a
              href="https://nfj2hhi1cw.feishu.cn/wiki/D8fwwc6mIiKOJbk9L37cZlzAnyb#share-Ubl2dVI3Nof5J5xj3Tzc82QNnNh"
              target="_blank"
              className="flex select-none items-center gap-2 text-sm text-annotation-foreground underline underline-offset-4 transition-colors hover:text-primary hover:underline"
            >
              <ZoteroIcon size={16} />
              <span>{messages.common.zoteroGuide}</span>
            </a>
          </div>
        ) : null}
      </div>
    );
  };

  // Button 模式：渲染按钮，点击打开弹窗
  if (variant === "button") {
    return (
      <>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            // 当父级 Dialog 关闭时，重置子 Dialog 状态
            setSubDialogOpen(false);
          }
        }}>
          <DialogTrigger asChild>
            <Button
              type="button"
              {...buttonProps}
              disabled={isSubmitting || buttonProps?.disabled}
              className={cn("min-w-[130px]", buttonClassName, buttonProps?.className)}
            >
              {label}
            </Button>
          </DialogTrigger>
          <DialogContent
            className={cn(
              "max-h-fit overflow-visible px-[60px] pt-[60px] pb-6",
              subDialogOpen && "hidden",
            )}
            style={{
              width: 720,
              maxWidth: "calc(100% - 2rem)",
            }}
            onDragOver={(e) => {
              // 允许拖拽事件传播到子元素，但不阻止默认行为
              e.preventDefault();
            }}
          >
            {/* Dialog 内部的上传区域 - 使用主体 input 统一处理本地/文件夹上传 */}
            {renderUploadArea({ includeOpenFilePicker: true })}
          </DialogContent>
        </Dialog>

        {/* 外部拖拽遮罩层 - 在组件外部渲染，无论 Dialog 是否打开 */}
        {externalDragging && renderExternalDragOverlay()}

        {/* 共享的文件模式 input（用于 LocalUploadArea 和 local-file-plugin） */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept={acceptAttr}
          disabled={isSubmitting}
          onChange={(event) => {
            const input = event.target;
            processLocalFiles(input.files, { fromDirectory: false });
            input.value = "";
          }}
        />
        {/* 共享的文件夹模式 input（用于 local-folder-plugin） */}
        <input
          ref={folderInputRef}
          type="file"
          className="hidden"
          multiple
          accept={acceptAttr}
          disabled={isSubmitting}
          {...({ webkitdirectory: "" } as any)}
          onChange={(event) => {
            const input = event.target;
            handleFolderSelection(input.files);
            input.value = "";
          }}
        />
      </>
    );
  }


  // Area 模式：直接渲染上传区域内容
  return (
    <>
      {renderUploadArea({ wrapperClassName: className })}

      {/* 外部拖拽遮罩层 */}
      {externalDragging && renderExternalDragOverlay()}

      {/* 共享的文件模式 input（用于 LocalUploadArea 和 local-file-plugin） */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={acceptAttr}
        disabled={isSubmitting}
        onChange={(event) => {
          const input = event.target;
          processLocalFiles(input.files, { fromDirectory: false });
          input.value = "";
        }}
      />
      {/* 共享的文件夹模式 input（用于 local-folder-plugin） */}
      <input
        ref={folderInputRef}
        type="file"
        className="hidden"
        multiple
        accept={acceptAttr}
        disabled={isSubmitting}
        {...({ webkitdirectory: "" } as any)}
        onChange={(event) => {
          const input = event.target;
          handleFolderSelection(input.files);
          input.value = "";
        }}
      />
    </>
  );
});

function pluginToSource(plugin: UploadPlugin): UploadItem["source"] {
  // 优先使用插件自身声明的来源；未声明时统一归类为 "plugin"
  return plugin.source ?? "plugin";
}

type UploadSelectionResult =
  | { files: File[]; exceeded: false }
  | { files: File[]; exceeded: true; errorMessage: string };

function collectUploadSelection(
  fileList: FileList | File[],
  options: {
    config: UploadConfig;
    uploadedCount: number;
    fromDirectory?: boolean;
  },
): UploadSelectionResult {
  const { config, uploadedCount, fromDirectory } = options;
  const remainingByTotal = (config.maxTotal ?? Infinity) - uploadedCount;
  const maxSelectableCount = Math.max(0, Math.min(config.maxFiles, remainingByTotal));
  const files: File[] = [];
  const length = fileList.length;

  for (let index = 0; index < length; index += 1) {
    const file = Array.isArray(fileList) ? fileList[index] : fileList.item(index);
    if (!file) {
      continue;
    }
    if (fromDirectory && isHiddenFile(file)) {
      continue;
    }
    files.push(file);

    if (files.length > maxSelectableCount) {
      const validation = validateCountBeforeUpload(files.length, config, uploadedCount);
      const errorMessage = validation.valid
        ? messages.errors.singleCountExceeded(config.maxFiles)
        : validation.error.message;

      return {
        files: [],
        exceeded: true,
        errorMessage,
      };
    }
  }

  return {
    files,
    exceeded: false,
  };
}

interface LocalUploadAreaProps {
  subjectId?: string;
  acceptsText: string;
  config: UploadConfig;
  pluginButtons: ReactNode;
  openFilePicker: (options?: { preferFolder?: boolean }) => void;
  openFolderPicker: () => void;
  hasLocalFilePlugin: boolean;
  hasLocalFolderPlugin: boolean;
  processLocalFiles: (fileList: FileList | File[] | null, options?: { fromDirectory?: boolean }) => Promise<void>;
  isCumulativeMode: boolean;
  disabled?: boolean;
}

function LocalUploadArea({
  acceptsText,
  config,
  pluginButtons,
  openFilePicker,
  openFolderPicker,
  hasLocalFilePlugin,
  hasLocalFolderPlugin,
  processLocalFiles,
  isCumulativeMode,
  disabled = false,
}: LocalUploadAreaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();

      if (disabled) {
        return;
      }

      // 如果点击的是插件按钮区域内的元素，不触发文件选择器
      // 通过检查点击目标是否在插件按钮容器内来判断
      const target = event.target as HTMLElement;
      const pluginButtonsContainer = (event.currentTarget as HTMLElement).querySelector('.flex.flex-wrap.gap-3');

      // 如果点击目标在插件按钮容器内，或者是按钮元素，则不触发文件选择器
      if (pluginButtonsContainer && (pluginButtonsContainer.contains(target) || target.closest('button'))) {
        return;
      }

      // 根据启用的插件决定触发哪个文件选择器
      // 优先级：local-file-plugin > local-folder-plugin > 默认文件模式
      if (hasLocalFilePlugin) {
        openFilePicker();
      } else if (hasLocalFolderPlugin) {
        openFolderPicker();
      } else {
        openFilePicker();
      }
    },
    [disabled, hasLocalFilePlugin, hasLocalFolderPlugin, openFilePicker, openFolderPicker]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();

      if (disabled) {
        return;
      }

      // 根据启用的插件决定触发哪个文件选择器
      if (hasLocalFilePlugin) {
        openFilePicker();
      } else if (hasLocalFolderPlugin) {
        openFolderPicker();
      } else {
        openFilePicker();
      }
    },
    [disabled, hasLocalFilePlugin, hasLocalFolderPlugin, openFilePicker, openFolderPicker]
  );

  // 处理拖拽事件
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    // 重新进入/持续悬停时，取消可能已排队的 dragleave 定时器，避免闪烁
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isDragging) {
      setIsDragging(true);
    }
  }, [disabled, isDragging]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    // 重新进入/持续悬停时，取消可能已排队的 dragleave 定时器，避免闪烁
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isDragging) {
      setIsDragging(true);
    }
  }, [disabled, isDragging]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 延迟设置，避免在子元素间移动时触发
    timeoutRef.current = setTimeout(() => {
      setIsDragging(false);
    }, 200);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDragging(false);

    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) {
      return;
    }

    // 检查是否是 URL 拖拽
    const droppedUrl = dataTransfer.getData("text/plain");
    if (droppedUrl) {
      toast.error(messages.uploadArea.urlDragNotSupported);
      return;
    }

    const dropHasDirectory = hasDirectoryInDataTransfer(e.nativeEvent);
    const hasFiles = dataTransfer.files && dataTransfer.files.length > 0;

    if (!dropHasDirectory && hasFiles) {
      // 纯文件拖拽
      await processLocalFiles(dataTransfer.files, {
        fromDirectory: false,
      });
      return;
    }

    if (dropHasDirectory) {
      // 拖拽的是文件夹（或包含文件夹），需要通过 entry 递归读取所有文件
      const filesFromEntries = await collectFilesFromDataTransfer(dataTransfer);
      if (!filesFromEntries.length && hasFiles) {
        // 回退：有 files 但没读到 entry，走统一入口做隐藏文件过滤和数量保护
        await processLocalFiles(dataTransfer.files, { fromDirectory: true });
        return;
      }
      if (filesFromEntries.length > 0) {
        await processLocalFiles(filesFromEntries, { fromDirectory: true });
        return;
      }
    }

    // 如果既没有文件也没有文件夹，可能是其他类型的拖拽，忽略
    if (!hasFiles && !dropHasDirectory) {
      return;
    }
  }, [disabled, processLocalFiles]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleAreaClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "py-[50px] px-16 w-full rounded-xl border border-dashed bg-[#F9FBFF] flex flex-col text-center items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors",
        disabled && "cursor-wait",
        isDragging
          ? "border-primary bg-[#F1F4FE]"
          : "border-[#D2D7E5]"
      )}
    >
      <UploadCloudIcon height="54" color={isDragging ? "#3B82F6" : "#D2D7E5"} className={cn("transition-colors", isDragging && "animate-bounce")} />
      <p className="mb-5 mt-6 text-muted-foreground select-none">
        <span className="text-lg font-medium text-nowrap">{messages.uploadArea.dragFilesOrFolder(acceptsText)}</span>
        <br />
        <span className="text-sm font-normal">
          {messages.uploadArea.limitHint(isCumulativeMode ? messages.uploadArea.total : messages.uploadArea.single, config.maxFiles, config.maxFileSize)}
        </span>
      </p>
      {pluginButtons ? <div className="flex flex-wrap gap-3 mt-4">{pluginButtons}</div> : null}
    </div>
  );
}

// 判断文件是否为隐藏文件（文件名或路径中包含以 . 开头的部分）
function isHiddenFile(file: File): boolean {
  // 检查文件路径中是否有以 . 开头的部分（隐藏文件/文件夹）
  // webkitRelativePath 格式：folder/subfolder/filename.pdf
  const path = file.webkitRelativePath || file.name;
  const pathParts = path.split('/');
  return pathParts.some(part => part.startsWith('.'));
}

function hasDirectoryInDataTransfer(e: DragEvent): boolean {
  const items = e.dataTransfer?.items;
  if (!items) {
    return false;
  }
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind !== "file") {
      continue;
    }
    const entry = (item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.();
    if (entry?.isDirectory) {
      return true;
    }
  }
  return false;
}

type FileSystemEntry = {
  isFile: boolean;
  isDirectory: boolean;
  file?: (callback: (file: File) => void) => void;
  createReader?: () => {
    readEntries: (callback: (entries: FileSystemEntry[]) => void) => void;
  };
};

async function collectFilesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const items = dataTransfer.items;
  if (!items) {
    return [];
  }

  const filePromises: Promise<File[]>[] = [];

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind !== "file") continue;
    const entry = (item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.();
    if (!entry) continue;
    filePromises.push(traverseFileSystemEntry(entry));
  }

  const results = await Promise.all(filePromises);
  return results.flat();
}

// 判断文件系统条目是否为隐藏文件/文件夹（名称以 . 开头）
function isHiddenEntry(entry: FileSystemEntry & { name?: string; fullPath?: string }): boolean {
  // 优先使用 name 属性，如果没有则尝试从 fullPath 提取
  const name = entry.name || (entry.fullPath ? entry.fullPath.split('/').pop() : '');
  return name ? name.startsWith('.') : false;
}

function traverseFileSystemEntry(entry: FileSystemEntry & { name?: string; fullPath?: string }): Promise<File[]> {
  return new Promise((resolve) => {
    // 跳过隐藏文件/文件夹
    if (isHiddenEntry(entry)) {
      resolve([]);
      return;
    }

    if (entry.isFile && entry.file) {
      entry.file((file) => {
        // 再次检查文件名（双重保险）
        if (file.name.startsWith('.')) {
          resolve([]);
        } else {
          resolve([file]);
        }
      });
      return;
    }

    if (entry.isDirectory && entry.createReader) {
      const reader = entry.createReader();
      const allFiles: File[] = [];

      const readEntries = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) {
            resolve(allFiles);
            return;
          }

          // 过滤掉隐藏条目后再递归处理
          const visibleEntries = entries.filter(e => !isHiddenEntry(e));
          const subPromises = visibleEntries.map((e) => traverseFileSystemEntry(e));
          const subFiles = await Promise.all(subPromises);
          subFiles.forEach((fs) => allFiles.push(...fs));
          readEntries();
        });
      };

      readEntries();
      return;
    }

    resolve([]);
  });
}
